/** A file attached to one model size, as stored in the shared library. */
export interface LibraryFile {
  id: string;
  size_id: string;
  file_name: string;
  file_extension: string;
  r2_key: string;
  size_bytes: number;
  uploaded_at: number;
}

/** One size of one model in the shared library, with its measurements. */
export interface LibrarySize {
  id: string;
  model_id: string;
  size_label: string;
  source_format: string;
  length_mm: number | null;
  barrel_diameter_mm: number | null;
  handle_diameter_mm: number | null;
  knob_diameter_mm: number | null;
  profile_points: number | null;
  auto_trim_confidence: number | null;
  created_at: number;
  updated_at: number;
}

/** A model family in the shared library, with all its known sizes. */
export interface LibraryModel {
  id: string;
  name: string;
  created_at: number;
  sizes: LibrarySize[];
}

/** Parsed model name + size label from a base file name like "ML243-33". */
export interface ParsedModelName {
  modelName: string;
  sizeLabel: string;
}

/**
 * Splits a base file name (no extension) into a model family name and a
 * size label, using the LAST hyphen as the split point — e.g.
 * "ML243-33" -> { modelName: "ML243", sizeLabel: "33" },
 * "ML243-PRO-33" -> { modelName: "ML243-PRO", sizeLabel: "33" }.
 * Falls back to using the whole name as the model with an "unknown" size
 * label if no hyphen is present.
 */
export function parseModelName(baseName: string): ParsedModelName {
  const lastHyphen = baseName.lastIndexOf('-');
  if (lastHyphen === -1) {
    return { modelName: baseName, sizeLabel: 'unknown' };
  }
  const modelName = baseName.slice(0, lastHyphen).trim();
  const sizeLabel = baseName.slice(lastHyphen + 1).trim();
  if (!modelName || !sizeLabel) {
    return { modelName: baseName, sizeLabel: 'unknown' };
  }
  return { modelName, sizeLabel };
}
